<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { planService } from "@/_services";
import { liveService } from "@/_services/live.service";
import { ensureAzureMapsSdk } from "../maps/azure-maps-sdk";
import { createLiveMapController } from "../maps/live-map-controller";
import {
  getAccountMapStyle,
  resolveMapStyle,
} from "../maps/map-style-utils";
import LivePanel from "./live/components/LivePanel.vue";
import MapStyleSelector from "../maps/MapStyleSelector.vue";
import MapZoomControls from "../maps/MapZoomControls.vue";
import { accountStore } from "@/_store/accountStore";

const mapRootRef = ref(null);
const isLoadingMap = ref(false);
const initError = ref("");
const liveDate = ref(new Date().toISOString().slice(0, 10));
const vehicles = ref([]);
const livePlans = ref([]);
const expandedPlanIds = ref([]);
const planRouteStates = ref({});
const visibleRouteKeys = ref([]);
const accountsStore = accountStore();

let mapController = null;
let renderTicket = 0;
let pendingFocusVehicleId = null;
let autoRefreshTimer = null;
let suppressVehicleWatch = false;
let suppressRouteWatch = false;
let hasCustomMapStyleSelection = false;
const currentMapStyle = ref(resolveMapStyle(""));

const displayVehicles = computed(() =>
  vehicles.value.map((vehicle, index) => normalizeVehicle(vehicle, index))
);
const displayPlans = computed(() =>
  livePlans.value.map((plan, index) => {
    const normalizedPlan = normalizeLivePlan(plan, index);
    const routeState = planRouteStates.value[normalizedPlan.id] || {};
    return {
      ...normalizedPlan,
      isExpanded: expandedPlanIds.value.includes(normalizedPlan.id),
      routeCount: Array.isArray(routeState.routes)
        ? routeState.routes.length
        : Number(normalizedPlan.routeCount || 0),
      routes: Array.isArray(routeState.routes) ? routeState.routes : [],
      routesError: routeState.error || "",
      routesLoading: routeState.loading === true,
    };
  })
);
const visibleRoutes = computed(() => {
  const keys = visibleRouteKeys.value;
  const result = [];
  for (const plan of displayPlans.value) {
    for (const route of plan.routes) {
      if (keys.includes(String(route?.key || ""))) result.push(route);
    }
  }
  return result;
});

function normalizeVehicle(vehicle, index = 0) {
  const source = vehicle && typeof vehicle === "object" ? vehicle : {};
  const lat = Number.parseFloat(source?.location?.lat);
  const lng = Number.parseFloat(source?.location?.lng);
  const history = Array.isArray(source?.history)
    ? source.history
        .map((point) => {
          const pointLat = Number.parseFloat(point?.lat);
          const pointLng = Number.parseFloat(point?.lng);
          return {
            lat: Number.isFinite(pointLat) ? pointLat : null,
            lng: Number.isFinite(pointLng) ? pointLng : null,
            timestamp: point?.timestamp || null,
            speed_kmh: point?.speed_kmh ?? null,
            heading: point?.heading ?? null,
          };
        })
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    : [];

  return {
    id: source.vehicle_id || `vehicle_${index}`,
    name: source.name,
    abbreviation: source.abbreviation,
    model: source.model,
    registration_number: source.registration_number,
    color: source.route_color_hex,
    route_color_hex: source.route_color_hex,
    createdAt: source.created_at,
    visible: source.visible !== false,
    location: {
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
    },
    history,
  };
}

async function loadLiveVehicles() {
  const nextVehicles = await liveService.getLiveLocations(liveDate.value);
  const previousVisibility = new Map(
    vehicles.value.map((vehicle) => [String(vehicle.vehicle_id), vehicle.visible !== false])
  );

  vehicles.value = nextVehicles.map((vehicle) => ({
    ...vehicle,
    visible: previousVisibility.has(String(vehicle.vehicle_id))
      ? previousVisibility.get(String(vehicle.vehicle_id))
      : vehicle.visible !== false,
  }));
}

async function loadInProgressPlans() {
  const response = await planService.getInProgressPlans(liveDate.value);
  livePlans.value = extractPlansFromResponse(response);

  const availablePlanIds = new Set(livePlans.value.map((plan, index) => normalizeLivePlan(plan, index).id));
  expandedPlanIds.value = expandedPlanIds.value.filter((planId) => availablePlanIds.has(planId));

  const nextRouteStates = {};
  Object.entries(planRouteStates.value).forEach(([planId, value]) => {
    if (availablePlanIds.has(planId)) {
      nextRouteStates[planId] = value;
    }
  });
  planRouteStates.value = nextRouteStates;

  if (visibleRouteKeys.value.length > 0) {
    visibleRouteKeys.value = visibleRouteKeys.value.filter((key) => {
      const planId = String(key || "").split(":")[0];
      return availablePlanIds.has(planId);
    });
  }
}

async function refreshLiveMap(options = {}) {
  const currentTicket = ++renderTicket;
  const preserveCamera = options?.preserveCamera === true;
  const showLoading = preserveCamera !== true;
  const previousVisibleRouteKeys = [...visibleRouteKeys.value];

  if (showLoading) {
    isLoadingMap.value = true;
    initError.value = "";
  }

  try {
    suppressVehicleWatch = true;
    suppressRouteWatch = true;
    await Promise.all([loadLiveVehicles(), loadInProgressPlans()]);
    await syncVehiclesOnMap(currentTicket, { preserveCamera });
    const routesChanged =
      previousVisibleRouteKeys.length !== visibleRouteKeys.value.length ||
      previousVisibleRouteKeys.some((key, index) => key !== visibleRouteKeys.value[index]);

    if (!preserveCamera || routesChanged) {
      await syncRoutesOnMap(currentTicket, { preserveCamera });
    }
  } catch (error) {
    initError.value = String(error?.message || error || "No se pudo cargar la vista live.");
  } finally {
    suppressVehicleWatch = false;
    suppressRouteWatch = false;
    if (currentTicket === renderTicket) {
      isLoadingMap.value = false;
    }
  }
}

async function ensureMapController() {
  if (mapController) return mapController;

  await ensureAzureMapsSdk();
  mapController = createLiveMapController({
    initialSubscriptionKey: process.env.VUE_APP_AZURE_MAPS_SUBSCRIPTION_KEY || "",
    initialStyle: currentMapStyle.value,
    mapElement: mapRootRef.value?.querySelector?.("#live-map"),
    rootElement: mapRootRef.value,
  });

  return mapController;
}

async function syncVehiclesOnMap(ticket = renderTicket, options = {}) {
  if (ticket !== renderTicket) return;
  await nextTick();
  if (ticket !== renderTicket) return;

  const controller = await ensureMapController();
  if (!controller || ticket !== renderTicket) return;

  const normalizedVehicles = vehicles.value
    .map((vehicle, index) => normalizeVehicle(vehicle, index))
    .filter(
      (vehicle) =>
        Number.isFinite(vehicle.location.lat) && Number.isFinite(vehicle.location.lng)
    );

  await controller.setVehicles(normalizedVehicles, options);

  if (pendingFocusVehicleId) {
    controller.focusVehicle(pendingFocusVehicleId);
    pendingFocusVehicleId = null;
  }
}

async function syncRoutesOnMap(ticket = renderTicket, options = {}) {
  if (ticket !== renderTicket) return;
  await nextTick();
  if (ticket !== renderTicket) return;

  const controller = await ensureMapController();
  if (!controller || ticket !== renderTicket) return;

  await controller.setComparisonRoutes(visibleRoutes.value.map((r) => r.route), options);
}

const onToggleVehicle = (vehicleId) => {
  const target = vehicles.value.find((v) => String(v.vehicle_id) === String(vehicleId));
  const willBeVisible = target?.visible === false;

  vehicles.value = vehicles.value.map((vehicle) =>
    String(vehicle.vehicle_id) === String(vehicleId)
      ? { ...vehicle, visible: vehicle.visible === false }
      : vehicle
  );

  if (willBeVisible) {
    pendingFocusVehicleId = vehicleId;
  }
};



function zoomIn() {
  mapController?.zoomIn();
}

function zoomOut() {
  mapController?.zoomOut();
}

function fitMap() {
  mapController?.fitMap();
}

function setMapStyle(style) {
  const nextStyle = resolveMapStyle(style);
  if (!nextStyle || nextStyle === currentMapStyle.value) {
    return;
  }

  hasCustomMapStyleSelection = true;
  currentMapStyle.value = nextStyle;
  renderTicket += 1;

  if (mapController) {
    mapController.destroy();
    mapController = null;
  }

  void refreshLiveMap();
}


function toSafeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function extractPlansFromResponse(response) {
  if (Array.isArray(response?.plans)) return response.plans;
  if (Array.isArray(response?.data?.plans)) return response.data.plans;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}

function extractRoutesFromResponse(response) {
  if (Array.isArray(response?.routes)) return response.routes;
  if (Array.isArray(response?.data?.routes)) return response.data.routes;
  if (Array.isArray(response?.plan?.routes)) return response.plan.routes;
  if (Array.isArray(response?.data?.plan?.routes)) return response.data.plan.routes;
  if (Array.isArray(response)) return response;
  return [];
}

function normalizeLivePlan(plan, index = 0) {
  const source = plan && typeof plan === "object" ? plan : {};
  return {
    id: toSafeString(source._id || source.id || `plan_${index + 1}`),
    name: toSafeString(source.name || source.plan_name || `Plan ${index + 1}`),
    routeCount: Number(source.route_count ?? source.routes_count ?? source.total_routes ?? 0) || 0,
  };
}

function normalizeLiveRoute(planItem, routeItem, index = 0) {
  const normalizedPlan = normalizeLivePlan(planItem);
  const source = routeItem && typeof routeItem === "object" ? routeItem : {};
  const routeId = toSafeString(source._id || source.id || source.vehicle_id || `route_${index + 1}`);
  const stops = Array.isArray(source.routes) ? source.routes : [];
  return {
    key: `${normalizedPlan.id}:${routeId}`,
    id: routeId,
    name: toSafeString(source.name || source.abbreviation || `Ruta ${index + 1}`),
    color: toRouteColor(source.route_color_hex),
    planId: normalizedPlan.id,
    planName: normalizedPlan.name,
    stopCount: stops.length,
    route: source,
  };
}

function toRouteColor(value) {
  const normalized = String(value || "")
    .trim()
    .replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return "#0ea5e9";
  return `#${normalized}`;
}

function resolveErrorMessage(error, fallback) {
  const message = toSafeString(error?.message || error?.detail || error?.error);
  if (message) return message;
  return fallback;
}

async function togglePlanRoutes(planId) {
  const normalizedPlanId = toSafeString(planId);
  if (!normalizedPlanId) return;

  if (expandedPlanIds.value.includes(normalizedPlanId)) {
    expandedPlanIds.value = expandedPlanIds.value.filter((item) => item !== normalizedPlanId);
    return;
  }

  expandedPlanIds.value = [...expandedPlanIds.value, normalizedPlanId];
  const existingState = planRouteStates.value[normalizedPlanId];
  if (existingState?.loaded || existingState?.loading) return;

  const planItem = displayPlans.value.find((plan) => plan.id === normalizedPlanId) || livePlans.value.find((plan, index) => normalizeLivePlan(plan, index).id === normalizedPlanId);
  planRouteStates.value = {
    ...planRouteStates.value,
    [normalizedPlanId]: {
      loaded: false,
      loading: true,
      error: "",
      routes: [],
    },
  };

  try {
    const response = await planService.getPlanMap(normalizedPlanId);
    const routes = extractRoutesFromResponse(response).map((route, index) =>
      normalizeLiveRoute(planItem, route, index)
    );
    planRouteStates.value = {
      ...planRouteStates.value,
      [normalizedPlanId]: {
        loaded: true,
        loading: false,
        error: "",
        routes,
      },
    };
  } catch (error) {
    planRouteStates.value = {
      ...planRouteStates.value,
      [normalizedPlanId]: {
        loaded: false,
        loading: false,
        error: resolveErrorMessage(error, "No se pudieron cargar las rutas del plan."),
        routes: [],
      },
    };
  }
}

function toggleRouteVisibility(routeItem) {
  const key = toSafeString(routeItem?.key);
  if (!key) return;
  const wasVisible = visibleRouteKeys.value.includes(key);

  suppressRouteWatch = true;
  if (wasVisible) {
    visibleRouteKeys.value = visibleRouteKeys.value.filter((k) => k !== key);
  } else {
    visibleRouteKeys.value = [...visibleRouteKeys.value, key];
  }
  suppressRouteWatch = false;

  void syncRoutesOnMap(renderTicket, { preserveCamera: wasVisible });
}

onMounted(() => {
  currentMapStyle.value = getAccountMapStyle(accountsStore.account);
  if (!accountsStore.account) {
    accountsStore.getAccount().catch(() => null);
  }
  void refreshLiveMap();
  autoRefreshTimer = window.setInterval(() => {
    void refreshLiveMap({ preserveCamera: true });
  }, 60_000);
});

watch(
  () => accountsStore.account,
  (value) => {
    if (!value || hasCustomMapStyleSelection) return;
    const nextStyle = getAccountMapStyle(value);
    if (nextStyle === currentMapStyle.value) return;
    currentMapStyle.value = nextStyle;
    renderTicket += 1;
    if (mapController) {
      mapController.destroy();
      mapController = null;
    }
    void refreshLiveMap();
  }
);

watch(liveDate, () => {
  void refreshLiveMap();
});

watch(vehicles, () => {
  if (suppressVehicleWatch) return;
  void syncVehiclesOnMap(renderTicket, { preserveCamera: true });
}, { deep: true });

watch(visibleRouteKeys, () => {
  if (suppressRouteWatch) return;
  void syncRoutesOnMap(renderTicket, { preserveCamera: true });
});

onBeforeUnmount(() => {
  renderTicket += 1;
  if (autoRefreshTimer) {
    window.clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
  if (mapController) {
    mapController.destroy();
    mapController = null;
  }
});
</script>

<template>
  <div class="container-fluid live-page-container">
    <div class="live-page-shell">
      <section ref="mapRootRef" class="live-map-shell">
        <div class="live-map-stage">
          <div class="live-map-overlay-controls">
            <div class="live-map-control-group live-map-control-group--zoom">
              <map-style-selector
                :model-value="currentMapStyle"
                @change="setMapStyle"
              />
              <map-zoom-controls
                @zoom-in="zoomIn"
                @zoom-out="zoomOut"
                @fit="fitMap"
              />
            </div>
          </div>

          <div id="live-map" class="live-map-canvas" role="application" aria-label="Mapa live de vehículos"></div>

          <div v-if="isLoadingMap" class="live-map-overlay">
            <div class="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true"></div>
          </div>

          <div v-if="initError" class="live-map-error alert alert-danger" role="alert">
            {{ initError }}
          </div>
        </div>

        <live-panel
          class="live-map-panel"
          :vehicles="displayVehicles"
          :plans="displayPlans"
          :visible-route-keys="visibleRouteKeys"
          @toggle-vehicle="onToggleVehicle"
          @toggle-plan="togglePlanRoutes"
          @toggle-route="toggleRouteVisibility"
        />
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
.live-page-container {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

.live-page-shell {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 16px;
  min-height: 0;
}

.live-map-shell {
  background: #fff;
  border: 1px solid #dce4f0;
  border-radius: 16px;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

.live-map-stage {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
}

.live-map-canvas {
  height: 100%;
  min-height: 0;
  width: 100%;
}

.live-map-overlay-controls {
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: 6;
}

.live-map-control-group {
  display: flex;
  flex-direction: row;
  gap: 6px;
  pointer-events: auto;
  position: absolute;
}

.live-map-control-group--zoom {
  align-items: center;
  bottom: 14px;
  flex-direction: column;
  right: 14px;
}

.live-map-control-btn {
  align-items: center;
  background: rgba(255, 253, 249, 0.95);
  border: 1px solid rgba(24, 33, 38, 0.12);
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(28, 24, 18, 0.12);
  color: #182126;
  display: inline-flex;
  font-size: 18px;
  font-weight: 600;
  height: 34px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  transition:
    background-color 180ms ease,
    color 180ms ease,
    border-color 180ms ease,
    box-shadow 220ms ease,
    transform 180ms ease;
  width: 34px;
}

.live-map-control-btn:hover {
  background: rgba(255, 255, 255, 0.98);
  border-color: rgba(24, 33, 38, 0.18);
}

.live-map-control-btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(94, 114, 228, 0.16);
  outline: none;
}

.live-map-control-btn--fit {
  font-size: 14px;
}

.live-map-overlay {
  left: 50%;
  position: absolute;
  top: 14px;
  transform: translateX(-50%);
  z-index: 7;
}

.live-map-overlay {
  align-items: center;
  background: rgba(248, 250, 252, 0.8);
  border-radius: 10px;
  display: inline-flex;
  padding: 8px 12px;
}

.live-map-error {
  background: rgba(180, 35, 24, 0.12);
  border: 1px solid rgba(180, 35, 24, 0.28);
  border-radius: 10px;
  color: #b42318;
  max-width: min(520px, calc(100% - 32px));
  padding: 10px 12px;
  text-align: center;
}

.live-map-panel {
  left: 14px;
  position: absolute;
  top: 14px;
  z-index: 6;
}


:deep(.live-marker-current) {
  align-items: center;
  background: transparent;
  border: 0;
  cursor: default;
  display: inline-flex;
  height: 36px;
  justify-content: center;
  padding: 0;
  position: relative;
  transform: translate(-50%, -50%);
  width: 36px;
}

:deep(.live-marker-pulse) {
  animation: live-marker-pulse 2s ease-out infinite;
  background: var(--live-marker-color);
  border-radius: 999px;
  inset: 0;
  opacity: 0.22;
  position: absolute;
}

:deep(.live-marker-core) {
  align-items: center;
  background: var(--live-marker-color);
  border: 2px solid rgba(255, 255, 255, 0.95);
  border-radius: 999px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.22);
  color: #fff;
  display: inline-flex;
  font-size: 0.58rem;
  font-weight: 800;
  height: 24px;
  justify-content: center;
  position: relative;
  width: 24px;
  z-index: 1;
}

@keyframes live-marker-pulse {
  0% {
    opacity: 0.5;
    transform: scale(0.9);
  }

  100% {
    opacity: 0;
    transform: scale(1.7);
  }
}

@media (max-width: 992px) {
  .live-map-shell {
    min-height: calc(100dvh - 160px);
  }

  .live-map-panel {
    left: 12px;
    right: 12px;
    top: 12px;
  }

  .live-map-control-group--zoom {
    align-items: flex-end;
    bottom: 12px;
    flex-direction: column;
  }
}
</style>
